/**
 * SmsCampaignsPage.tsx
 *
 * SMS campaign management: list with stats/filters, create flow,
 * and campaign stats view. The campaign editor has been extracted
 * to SmsCampaignEditorPage.tsx at /marketing/sms-campaigns/:id.
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus, Send, Clock, MessageCircle, ArrowLeft,
  Eye, FileText, Search, Copy,
} from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { FilterTabs } from '@/components/ui/FilterTabs';
import type { BadgeVariant } from '@/types/common.types';
import { usePageContext } from '@/hooks/usePageContext';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsCampaign, SmsMarketingTemplate } from '@/services/marketing.service';
import SmsTemplatePickerModal from '@/components/sms-editor/SmsTemplatePickerModal';
import { ensureSmsOptOut } from '@/components/sms-editor/sms-editor-constants';
import { CloneCampaignModal } from './CloneCampaignModal';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtDateTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─── Views ──────────────────────────────────────────────────────────────────

type View =
  | { mode: 'list' }
  | { mode: 'create' }
  | { mode: 'stats'; id: number };

type NavTarget = View | { mode: 'edit'; id: number };

// ═══════════════════════════════════════════════════════════════════════════
// Root Component
// ═══════════════════════════════════════════════════════════════════════════

export default function SmsCampaignsPage() {
  const [view, setView] = useState<View>({ mode: 'list' });
  const navigate = useNavigate();
  const updateAIContext = usePageContext('SMS Campaigns');

  useEffect(() => {
    updateAIContext({
      filters: `View: ${view.mode}${view.mode === 'stats' ? `, Campaign ID: ${(view as any).id}` : ''}`,
      dataSummary: `Current view: ${view.mode}`,
    });
  }, [view, updateAIContext]);

  const handleNavigate = (v: NavTarget) => {
    if (v.mode === 'edit') {
      navigate(`/marketing/sms-campaigns/${v.id}`);
    } else {
      setView(v);
    }
  };

  switch (view.mode) {
    case 'list':
      return <CampaignListView onNavigate={handleNavigate} />;
    case 'create':
      return <CampaignCreateView onNavigate={handleNavigate} />;
    case 'stats':
      return <CampaignStatsView id={view.id} onNavigate={handleNavigate} />;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 1) Campaign List View
// ═══════════════════════════════════════════════════════════════════════════

function CampaignListView({ onNavigate }: { onNavigate: (v: NavTarget) => void }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', { search, status: statusFilter, page }],
    queryFn: () => marketingSvc.listSmsCampaigns({ search, status: statusFilter || undefined, page, perPage: 25 }),
  });

  const { data: listStats } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', 'stats'],
    queryFn: () => marketingSvc.getSmsCampaignListStats(),
    staleTime: 60_000,
  });

  const campaigns = data?.data ?? [];
  const meta = data?.meta;

  const draftCount     = listStats?.draft     ?? 0;
  const scheduledCount = listStats?.scheduled ?? 0;
  const sentCount      = listStats?.sent      ?? 0;

  const columns: DataTableColumn<SmsCampaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      render: (r) => (
        <div>
          <span className="font-medium text-slate-900">{r.name}</span>
          {r.description && (
            <span className="block text-xs text-slate-500 truncate max-w-xs">{r.description}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'neutral'} dot>
          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
        </Badge>
      ),
    },
    { key: 'totalRecipients', header: 'Recipients', render: (r) => (r.totalRecipients ?? 0).toLocaleString() },
    {
      key: 'sentAt',
      header: 'Scheduled / Sent',
      render: (r) => {
        if (r.sentAt) {
          const formatted = fmtDate(r.sentAt);
          if (formatted) return <span className="text-sm text-slate-600">{formatted}</span>;
        }
        if (r.scheduledAt) {
          const formatted = fmtDate(r.scheduledAt);
          if (formatted) return <span className="text-sm text-amber-600">{formatted}</span>;
        }
        return null;
      },
    },
    { key: 'createDate', header: 'Created', render: (r) => fmtDate(r.createDate) },
  ];

  function handleRowClick(r: SmsCampaign) {
    if (r.status === 'scheduled') {
      // Scheduled campaigns are locked; only Cancel Send is offered there.
      navigate(`/marketing/sms-campaigns/${r.smsCampaignId}/scheduled`);
      return;
    }
    if (r.status === 'sent' || r.status === 'sending') {
      navigate(`/marketing/sms-campaigns/${r.smsCampaignId}/stats`);
      return;
    } else {
      onNavigate({ mode: 'edit', id: r.smsCampaignId });
    }
  }

  const filterTabs = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Sent', value: 'sent' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0 space-y-6">
        <PageHeader
          variant="compact"
          title="SMS Campaigns"
          actions={(
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => onNavigate({ mode: 'create' })}>
              New Campaign
            </Button>
          )}
        />

        {/* Stats row */}
        <MetricCardGrid columns={4} className="grid-cols-2">
          <StatCard label="Total" value={listStats?.total ?? meta?.total ?? 0} icon={<MessageCircle className="h-5 w-5" />} tone="sky" />
          <StatCard label="Draft" value={draftCount} icon={<FileText className="h-5 w-5" />} tone="slate" />
          <StatCard label="Scheduled" value={scheduledCount} icon={<Clock className="h-5 w-5" />} tone="amber" />
          <StatCard label="Sent" value={sentCount} icon={<Send className="h-5 w-5" />} tone="emerald" />
        </MetricCardGrid>

        {/* Filter tabs + search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <FilterTabs
            tabs={filterTabs}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-6">
        <DataTable
          keyField="smsCampaignId"
          columns={columns}
          data={campaigns}
          loading={isLoading}
          page={meta?.page ?? 1}
          pageSize={meta?.perPage ?? 25}
          total={meta?.total ?? 0}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          emptyTitle="No SMS campaigns yet"
          emptyDescription="Create your first SMS campaign to get started."
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2) Campaign Create View
// ═══════════════════════════════════════════════════════════════════════════

function CampaignCreateView({ onNavigate }: { onNavigate: (v: NavTarget) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [newCampaignId, setNewCampaignId] = useState<number | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const createMut = useMutation({
    mutationFn: (d: { name: string; description?: string }) => marketingSvc.createSmsCampaign(d),
    onSuccess: (campaign) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      toast.success('Campaign created! Now select a template.');
      setNewCampaignId(campaign.smsCampaignId);
      setTemplatePickerOpen(true);
    },
    onError: () => toast.error('Failed to create campaign'),
  });

  // Template chosen (step 2) -> copy its content onto the new campaign, then open the editor
  async function handleTemplateApply(tpl: SmsMarketingTemplate) {
    if (!newCampaignId || applyingTemplate) return;
    setApplyingTemplate(true);
    try {
      await marketingSvc.updateSmsCampaign(newCampaignId, {
        // A new campaign starts life with opt-out instructions, whatever the
        // template carried (older templates predate the rule).
        messageBody: ensureSmsOptOut(tpl.messageBody ?? ''),
        mediaUrls: tpl.mediaUrls ?? [],
      });
      toast.success('Template applied!');
    } catch {
      toast.warning('Template could not be applied, but the campaign was created.');
    }
    qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
    setTemplatePickerOpen(false);
    onNavigate({ mode: 'edit', id: newCampaignId });
  }

  // Picker dismissed -> the campaign still exists, so go straight to the editor
  function handleSkipTemplate() {
    if (applyingTemplate) return;
    setTemplatePickerOpen(false);
    if (newCampaignId) onNavigate({ mode: 'edit', id: newCampaignId });
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate({ mode: 'list' })} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <h1 className="text-xl font-semibold text-slate-900">Create SMS Campaign</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => onNavigate({ mode: 'list' })}>Cancel</Button>
          <Button
            loading={createMut.isPending}
            disabled={!name.trim()}
            onClick={() => createMut.mutate({ name: name.trim(), description: description.trim() || undefined })}
          >
            Next: Choose Template
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            <span className="font-semibold text-slate-800">SMS Campaign Details</span>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <FormField label="Campaign Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Holiday Promo SMS"
            />
          </FormField>
          <FormField label="Description" helpText="For internal reference only -- not visible to recipients">
            <Textarea autoResize
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Internal notes about this campaign..."
            />
          </FormField>
        </CardBody>
      </Card>

      {/* Template picker modal (step 2) */}
      <SmsTemplatePickerModal
        isOpen={templatePickerOpen}
        onClose={handleSkipTemplate}
        onApply={handleTemplateApply}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3) Campaign Stats View
// ═══════════════════════════════════════════════════════════════════════════

function CampaignStatsView({ id, onNavigate }: { id: number; onNavigate: (v: NavTarget) => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showPreview, setShowPreview] = useState(false);
  
  const [showCloneModal, setShowCloneModal] = useState(false);

  const cloneMut = useMutation({
    mutationFn: (cloneName: string) => marketingSvc.cloneSmsCampaign(id, cloneName),
    onSuccess: (data) => {
      toast.success('Campaign cloned successfully');
      setShowCloneModal(false);
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      navigate(`/marketing/sms-campaigns/${data.smsCampaignId}`);
    },
    onError: () => toast.error('Failed to clone campaign'),
  });

  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id],
    queryFn: () => marketingSvc.getSmsCampaign(id),
  });

  const { data: stats } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'stats'],
    queryFn: () => marketingSvc.getSmsCampaignStats(id),
    enabled: !!campaign,
  });

  const { data: campaignLists = [] } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'lists'],
    queryFn: () => marketingSvc.getSmsCampaignLists(id),
    enabled: !!campaign,
  });

  

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16 text-slate-500">
        Campaign not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={() => onNavigate({ mode: 'list' })}>Back to Campaigns</Button>
        </div>
      </div>
    );
  }

  const totalSent = stats?.stats?.totalSent ?? 0;
  const totalDelivered = stats?.stats?.totalDelivered ?? 0;
  const totalFailed = stats?.stats?.totalFailed ?? 0;
  const totalUndelivered = stats?.stats?.totalUndelivered ?? 0;
  const contactsCount = campaign.totalRecipients ?? 0;

  

  return (
    <div className="space-y-6 p-5">
      {/* Header */}
      <div className="flex items-start justify-between pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => onNavigate({ mode: 'list' })} className="p-1 rounded hover:bg-slate-100">
              <ArrowLeft className="h-5 w-5 text-slate-500" />
            </button>
            <h1 className="text-2xl font-semibold text-slate-800">{campaign.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={STATUS_BADGE[campaign.status] ?? 'neutral'} dot>
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </Badge>
            <span className="text-sm text-slate-500">
              {campaign.sentAt
                ? `Sent on ${fmtDateTime(campaign.sentAt)}`
                : `Created ${fmtDateTime(campaign.createDate)}`}
            </span>
          </div>
          {campaign.description && (
            <p className="mt-2 text-sm text-slate-500">{campaign.description}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-4">
            <Link
              to={`/marketing/sms-campaigns/${id}/sent-contacts`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-sky-50 transition-colors"
            >
              <span className="text-xl font-bold text-sky-500">{contactsCount.toLocaleString()}</span>
              <span className="text-sm font-semibold text-slate-700">
                {contactsCount === 1 ? 'contact' : 'contacts'}
              </span>
            </Link>
            <Button variant="ghost" size="sm" onClick={() => setShowCloneModal(true)}>
              <Copy className="h-4 w-4 mr-1" /> Clone Campaign
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4 mr-1" /> View Content
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <MetricCardGrid columns={4}>
        <StatCard label="SMS Sent" value={totalSent} tone="sky" />
        <StatCard label="Delivered" value={totalDelivered} tone="green" />
        <StatCard label="Failed" value={totalFailed} tone="red" />
        <StatCard label="Undelivered" value={totalUndelivered} tone="red" />
      </MetricCardGrid>

      {/* Campaign Details */}
      <Card>
        <CardHeader>Details</CardHeader>
        <CardBody className="p-0">
          <div className="grid grid-cols-[200px_1fr]">
            {[
              ['Campaign Name', campaign.name],
              campaign.description ? ['Description', campaign.description] : null,
              ['From Number', campaign.fromNumber ?? '\u2014'],
              campaign.sentAt ? ['Sent At', fmtDateTime(campaign.sentAt)] : null,
            ]
              .filter(Boolean)
              .map((row, i) => (
                <React.Fragment key={i}>
                  <div className="px-5 py-3.5 font-semibold text-sm text-slate-700 bg-slate-50 border-b border-slate-100">
                    {(row as string[])[0]}
                  </div>
                  <div className="px-5 py-3.5 text-sm text-slate-600 border-b border-slate-100">
                    {(row as string[])[1]}
                  </div>
                </React.Fragment>
              ))}

            {/* Lists row */}
            {campaignLists.length > 0 && (
              <>
                <div className="px-5 py-3.5 font-semibold text-sm text-slate-700 bg-slate-50 border-b border-slate-100">
                  Lists
                </div>
                <div className="px-5 py-3.5 border-b border-slate-100">
                  <div className="flex flex-wrap gap-2">
                    {campaignLists.map((li) => (
                      <span
                        key={li.emailListId}
                        className="inline-block px-2.5 py-1 bg-sky-50 text-sky-600 rounded text-sm"
                      >
                        {li.listName}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Recipients row */}
            <div className="px-5 py-3.5 font-semibold text-sm text-slate-700 bg-slate-50">Recipients</div>
            <div className="px-5 py-3.5">
              <Link
                to={`/marketing/sms-campaigns/${id}/sent-contacts`}
                className="flex items-center gap-2 text-sky-500 hover:underline"
              >
                <span className="text-lg font-semibold">{contactsCount.toLocaleString()}</span>
                <span className="text-sm">{contactsCount === 1 ? 'contact' : 'contacts'}</span>
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Content Preview Modal ── */}
      <Modal isOpen={showPreview} onClose={() => setShowPreview(false)} title="SMS Content Preview" size="sm">
        <div className="flex justify-center py-4">
          <StatsPhoneMockup
            messageBody={campaign.messageBody ?? ''}
            mediaUrls={campaign.mediaUrls ?? []}
          />
        </div>
      </Modal>

      

      <CloneCampaignModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={(cloneName) => cloneMut.mutate(cloneName)}
        loading={cloneMut.isPending}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phone Mockup (minimal version for stats preview)
// ═══════════════════════════════════════════════════════════════════════════

function StatsPhoneMockup({
  messageBody,
  mediaUrls,
}: {
  messageBody: string;
  mediaUrls: string[];
}) {
  return (
    <div className="w-[280px] bg-slate-100 rounded-[30px] border-[3px] border-slate-300 p-2.5 shadow-md">
      <div className="bg-white rounded-[22px] overflow-hidden flex flex-col h-[520px]">
        <div className="px-4 py-2.5 text-xs text-slate-400 text-center border-b border-slate-50 flex-shrink-0">
          SMS Preview
        </div>
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {mediaUrls.map((url, i) => (
            <div key={i} className="max-w-[85%] mb-2 rounded-xl overflow-hidden">
              <img src={url} alt="Media" className="w-full block" />
            </div>
          ))}
          <div className="max-w-[85%] px-3.5 py-2.5 bg-slate-200 rounded-2xl rounded-bl text-sm leading-relaxed whitespace-pre-wrap break-words text-slate-700">
            {messageBody || 'Your message will appear here...'}
          </div>
        </div>
      </div>
    </div>
  );
}
